/**
 * @fileoverview Calendar sync (Spec 19 §5): one chooser, Google Calendar ·
 * Apple Calendar · Other calendar app, in every portal.
 *
 * The same rows sit in four places and one dialog: the instructor's Settings
 * (and the Calendar page's dialog), the student Profile, Your family, the org's
 * Settings › Schedule, and a program viewer's Calendar tab. Nothing connected
 * is "Sync to your calendar" and three equal choices, none highlighted. Each
 * connection then shows its own row, and "Add another calendar" offers what is
 * left. Google is Hans's own sign-in window (`lib/calendarSync.js`); Apple and
 * Other on the web are the one private link. The words are the spec's, the
 * same on every client: Connected, Disconnect, Sync now, Last synced, Reset
 * link, Subscribe in Apple Calendar, Add another calendar, Calendar synced.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CalendarSync, Check, Link2 } from "lucide-react";
import { toast } from "sonner";

import { Button, ButtonLink } from "@/components/studio/primitives";
import { SettingsRow, SettingsSection } from "@/components/settings/SettingsSection";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/api";
import {
  CALENDAR_SYNC_CHANGED,
  GOOGLE_ERRORS,
  SYNC_DESCRIPTIONS,
  announceChange,
  awaitSignIn,
  choicesFor,
  googleReturn,
  isSynced,
  lastSynced,
  linkCreated,
  openSignInWindow,
  schedulerSkipLine,
} from "@/lib/calendarSync";
import { cn } from "@/lib/utils";

/** Google's "G", in its own colours and unaltered (Google's branding rules). */
export function GoogleLogo({ size = 17 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" className="shrink-0">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

/**
 * Apple's mark, in the text colour so it reads in both themes. Kept by John
 * (Spec 19 §5) despite the trademark risk; if App Review objects, a neutral
 * calendar glyph takes its place and nothing else changes.
 */
export function AppleLogo({ size = 17 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="shrink-0" fill="currentColor">
      <path d="M16.37 12.6c-.03-2.66 2.17-3.94 2.27-4-1.24-1.81-3.16-2.06-3.84-2.08-1.63-.17-3.19.96-4.02.96-.84 0-2.11-.94-3.47-.91-1.78.03-3.43 1.04-4.35 2.63-1.86 3.22-.47 7.98 1.33 10.6.89 1.28 1.94 2.71 3.32 2.66 1.33-.05 1.84-.86 3.45-.86 1.6 0 2.06.86 3.47.83 1.43-.02 2.34-1.3 3.21-2.59 1.02-1.48 1.43-2.92 1.45-2.99-.03-.02-2.79-1.07-2.82-4.25zM13.73 4.79c.73-.89 1.23-2.12 1.09-3.35-1.06.04-2.34.71-3.1 1.59-.68.78-1.27 2.04-1.12 3.24 1.18.09 2.39-.6 3.13-1.48z" />
    </svg>
  );
}

const CHOICES = {
  google: { label: "Google Calendar", logo: <GoogleLogo /> },
  apple: { label: "Apple Calendar", logo: <AppleLogo /> },
  other: { label: "Other calendar app", logo: <Link2 size={17} strokeWidth={2} className="shrink-0" /> },
};

/** The calendar with two sync arrows; once something is synced, a quiet green with a check. */
export function SyncIcon({ synced = false, size = 18 }) {
  return (
    <span className={cn("relative inline-flex shrink-0", synced && "text-[var(--state-success)]")}>
      <CalendarSync size={size} strokeWidth={1.9} />
      {synced && (
        <span
          className="absolute -bottom-1 -right-1 flex h-[11px] w-[11px] items-center justify-center rounded-full bg-[var(--state-success)] text-[var(--studio-base)] ring-2 ring-[var(--studio-raised)]"
          aria-hidden="true"
        >
          <Check size={8} strokeWidth={3.4} />
        </span>
      )}
    </span>
  );
}

const small = "px-3.5 py-2 text-[13px]";

function ConnectedTag() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[var(--state-success-border)] bg-[var(--state-success-dim)] px-2.5 py-0.5 font-sans text-[12px] font-semibold text-[var(--state-success)]">
      <Check size={12} strokeWidth={2.6} /> Connected
    </span>
  );
}

function NotSyncingTag() {
  return (
    <span className="inline-flex items-center rounded-full border border-[var(--state-warning-border)] bg-[var(--state-warning-dim)] px-2.5 py-0.5 font-sans text-[12px] font-semibold text-[var(--state-warning)]">
      Not syncing
    </span>
  );
}

/**
 * What one portal has connected. `undefined` while loading, `null` when
 * nothing is switched on (or the read failed), so the caller shows nothing.
 * Every mounted view of the portal reloads after any change.
 */
export function useCalendarSync({ client, prefix, available }) {
  const on = Boolean(available?.link || available?.google);
  const [sync, setSync] = useState(on ? undefined : null);
  const load = useCallback(async () => {
    if (!on) return setSync(null);
    try {
      const { data } = await client.get(`${prefix}/calendar-sync`);
      setSync(data || null);
    } catch {
      setSync(null);
    }
  }, [client, prefix, on]);
  useEffect(() => {
    load();
    window.addEventListener(CALENDAR_SYNC_CHANGED, load);
    return () => window.removeEventListener(CALENDAR_SYNC_CHANGED, load);
  }, [load]);
  return sync;
}

/** Trade a Google sign-in's `ref` for a connection, under this portal's session. */
async function finishGoogle(client, prefix, { ref, error }) {
  if (error) {
    toast.error(GOOGLE_ERRORS[error] || GOOGLE_ERRORS.failed);
    return false;
  }
  try {
    await client.post(`${prefix}/calendar-sync/google/finish`, { ref });
    toast.success("Google Calendar connected. Your calendar is syncing.");
    return true;
  } catch (err) {
    const code = err?.response?.data?.detail?.code;
    toast.error(code === "google_sign_in_expired" ? GOOGLE_ERRORS.expired : getErrorMessage(err, GOOGLE_ERRORS.failed));
    return false;
  } finally {
    announceChange();
  }
}

/* A full-page return is read once, even when two views of the page mount. */
const handledReturns = new Set();

/** When a blocked window sent the whole page to Google, finish here on return. */
function useGoogleReturn({ client, prefix }) {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const back = googleReturn(location.search);
    if (!back) return;
    const key = `${location.pathname}${location.search}`;
    if (handledReturns.has(key)) return;
    handledReturns.add(key);
    navigate({ pathname: location.pathname, search: back.search, hash: location.hash }, { replace: true });
    finishGoogle(client, prefix, back);
  }, [client, prefix, location.pathname, location.search, location.hash, navigate]);
}

/** Is there anything to show: a connection, or a choice to make? */
const hasRows = (sync, available) => Boolean(sync && (sync.google || sync.feed || choicesFor(sync, available).length));

/**
 * The rows for one loaded `sync`, with no section around them: Settings wraps
 * them in a card and the dialog shows them bare.
 */
export function CalendarSyncRows({ portal = "instructor", client, prefix, available, sync, last = true, titled = true }) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef(null);
  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const act = async (work, success, fallback) => {
    setBusy(true);
    try {
      const result = await work();
      if (success) toast.success(success);
      return result;
    } catch (err) {
      toast.error(getErrorMessage(err, fallback));
      return null;
    } finally {
      setBusy(false);
      announceChange();
    }
  };

  const connectGoogle = async () => {
    // Opened before the call: a window opened after it is a blocked popup.
    const popup = openSignInWindow();
    setBusy(true);
    try {
      const { data } = await client.post(`${prefix}/calendar-sync/google/start`, { client: "web" });
      if (!popup || popup.closed) {
        window.location.assign(data.url);
        return;
      }
      popup.location.href = data.url;
      const back = await awaitSignIn(popup);
      if (!back.closed) await finishGoogle(client, prefix, back);
    } catch (err) {
      popup?.close();
      toast.error(getErrorMessage(err, "Couldn’t open Google’s sign-in. Try again."));
    } finally {
      setBusy(false);
    }
  };

  const makeLink = async (id) => {
    const feed = await act(
      async () => (await client.post(`${prefix}/calendar-feed`)).data,
      id === "apple" ? "Your link is ready. Apple Calendar opens to subscribe." : "Your link is ready to copy into your calendar app.",
      "Couldn’t make your calendar link. Try again.",
    );
    // Calendar on a Mac (or an iPhone's Safari) asks to subscribe.
    if (feed?.webcal_url && id === "apple") window.location.assign(feed.webcal_url);
  };

  const choose = (id) => (id === "google" ? connectGoogle() : makeLink(id));

  if (!hasRows(sync, available)) return null;

  const { google, feed } = sync;
  const choices = choicesFor(sync, available);

  const choiceButtons = (ids, compact = false) => (
    <div className="flex flex-wrap gap-2.5">
      {/* Every choice weighs the same: none is the recommended one. */}
      {ids.map((id) => (
        <Button
          key={id}
          variant="ghost"
          disabled={busy}
          onPress={() => choose(id)}
          data-testid={`calendar-choice-${id}`}
          className={compact ? small : undefined}
        >
          {CHOICES[id].logo}
          {CHOICES[id].label}
        </Button>
      ))}
    </div>
  );

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(feed.url);
      setCopied(true);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn’t copy. Select the link and copy it instead.");
    }
  };

  const nothing = !google && !feed;
  const skipLine = portal === "instructor" && google ? schedulerSkipLine(google.scheduler_skips) : null;
  const active = google?.status === "active";

  return (
    <>
      {nothing && (
        <SettingsRow
          testId="calendar-sync-choose"
          title={titled ? "Sync to your calendar" : null}
          desc={SYNC_DESCRIPTIONS[portal]}
          align="start"
          last={last}
        >
          <div className="mt-4">{choiceButtons(choices)}</div>
        </SettingsRow>
      )}

      {google && (
        <SettingsRow
          testId="calendar-sync-google"
          title={
            <span className="inline-flex flex-wrap items-center gap-2">
              <GoogleLogo size={16} /> Google Calendar {active ? <ConnectedTag /> : <NotSyncingTag />}
            </span>
          }
          desc={
            active ? (
              <>
                Syncing to{" "}
                {google.calendar_name && (
                  <>
                    <span className="font-medium text-[var(--text-secondary)]">{google.calendar_name}</span> in{" "}
                  </>
                )}
                {google.email || "your Google account"} · {lastSynced(google.last_push_at)}.
                {skipLine && <span className="mt-1 block">{skipLine}</span>}
              </>
            ) : (
              "Google stopped accepting Hans’s updates. Reconnect to start again."
            )
          }
          align="start"
          last={last && !feed && !choices.length}
        >
          <div className="mt-4 flex flex-wrap gap-2">
            {active && available?.google && (
              <Button
                variant="bare"
                className={small}
                icon="refresh"
                disabled={busy}
                data-testid="calendar-google-sync-now"
                onPress={() => act(() => client.post(`${prefix}/calendar-sync/google/push`), "Syncing now.", "Couldn’t start a sync. Try again.")}
              >
                Sync now
              </Button>
            )}
            {!active && available?.google && (
              <Button variant="ghost" className={small} disabled={busy} data-testid="calendar-google-reconnect" onPress={connectGoogle}>
                <GoogleLogo size={15} /> Reconnect
              </Button>
            )}
            <Button
              variant="bare"
              className={small}
              disabled={busy}
              data-testid="calendar-google-disconnect"
              onPress={() =>
                act(
                  () => client.delete(`${prefix}/calendar-sync/google`),
                  "Google Calendar disconnected. Hans removed its calendar.",
                  "Couldn’t reach Google. Try again.",
                )
              }
            >
              Disconnect
            </Button>
          </div>
        </SettingsRow>
      )}

      {feed && (
        <SettingsRow
          testId="calendar-sync-link"
          title={
            <span className="inline-flex flex-wrap items-center gap-2">
              <AppleLogo size={16} /> Apple Calendar and other apps <ConnectedTag />
            </span>
          }
          desc="Calendar on a Mac or iPhone subscribes to this private link; Outlook and other apps add it by URL. Anyone with the link can see these times."
          align="start"
          last={last && !choices.length}
        >
          <div className="mt-4 min-w-0">
            <div className="flex flex-wrap gap-2.5">
              <ButtonLink href={feed.webcal_url} data-testid="calendar-link-subscribe">
                <AppleLogo /> Subscribe in Apple Calendar
              </ButtonLink>
              <Button variant="ghost" icon={copied ? "check" : "copy"} onPress={copyLink} data-testid="calendar-link-copy">
                {copied ? "Copied" : "Copy link"}
              </Button>
            </div>
            <div
              className="mt-3 truncate rounded-xl border border-[var(--studio-divider)] bg-[var(--studio-base)] px-3.5 py-2 font-mono text-[12px] text-[var(--text-tertiary)]"
              data-testid="calendar-link-url"
            >
              {feed.url}
            </div>
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <span className="font-sans text-[13px] text-[var(--text-tertiary)]">{linkCreated(feed)}</span>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="bare"
                  className={small}
                  icon="refresh"
                  disabled={busy}
                  data-testid="calendar-link-reset"
                  onPress={() =>
                    act(
                      () => client.post(`${prefix}/calendar-feed/rotate`),
                      "Link reset. Calendars using the old link stop updating.",
                      "Couldn’t reset the link. Try again.",
                    )
                  }
                >
                  Reset link
                </Button>
                <Button
                  variant="bare"
                  className={small}
                  disabled={busy}
                  data-testid="calendar-link-disconnect"
                  onPress={() =>
                    act(
                      () => client.delete(`${prefix}/calendar-feed`),
                      "Disconnected. Calendars using the link stop updating.",
                      "Couldn’t disconnect the link. Try again.",
                    )
                  }
                >
                  Disconnect
                </Button>
              </div>
            </div>
          </div>
        </SettingsRow>
      )}

      {!nothing && choices.length > 0 && (
        <SettingsRow testId="calendar-sync-another" title="Add another calendar" align="start" last={last}>
          <div className="mt-3">{choiceButtons(choices, true)}</div>
        </SettingsRow>
      )}
    </>
  );
}

/**
 * The Settings section ("Calendar sync"): a title over one card of the rows.
 * Shows nothing when no choice is switched on and nothing is connected, and
 * finishes a full-page return from Google either way.
 */
export default function CalendarSyncSection({ title = "Calendar sync", className, ...props }) {
  const sync = useCalendarSync(props);
  useGoogleReturn(props);
  if (!hasRows(sync, props.available)) return null;
  return (
    <SettingsSection id="calendar-sync" title={title} testId="calendar-sync-section" className={className}>
      <CalendarSyncRows {...props} sync={sync} />
    </SettingsSection>
  );
}

/** The rows in a dialog: the Calendar page's and a viewer's way in, so nobody leaves the calendar. */
export function CalendarSyncDialog({ open, onOpenChange, ...props }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-2rem)] max-w-[680px] gap-0 border-[var(--studio-divider-strong)] bg-[var(--studio-overlay)] p-0 text-[var(--text-primary)] sm:max-w-[680px]"
        data-testid="calendar-sync-dialog"
        // Focus the dialog, not its first choice: a ringed Google Calendar
        // would read as the recommended one, and none is.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          event.currentTarget?.focus?.();
        }}
      >
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="font-serif text-2xl font-normal tracking-[-0.01em]">Sync to your calendar</DialogTitle>
          <DialogDescription className="sr-only">Google Calendar, Apple Calendar or another calendar app.</DialogDescription>
        </DialogHeader>
        <div className="min-w-0 overflow-hidden pb-2">
          {/* The dialog's title already says "Sync to your calendar". */}
          <CalendarSyncRows {...props} titled={false} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The Calendar page's way in. Nothing synced: "Sync to your calendar" opens the
 * dialog. Synced: the icon turns a quiet green with a check and the button,
 * "Calendar synced", opens Calendar sync in Settings, where connections are
 * seen and managed. Without a Settings page (a program viewer) it always opens
 * the dialog. Also finishes a full-page return from Google.
 */
export function CalendarSyncButton({ settingsPath = null, ...props }) {
  const navigate = useNavigate();
  const sync = useCalendarSync(props);
  useGoogleReturn(props);
  const [open, setOpen] = useState(false);
  if (!hasRows(sync, props.available)) return null;
  const synced = isSynced(sync);
  return (
    <>
      <Button
        variant="ghost"
        onPress={synced && settingsPath ? () => navigate(settingsPath) : () => setOpen(true)}
        data-testid="calendar-sync-open"
        aria-label={synced ? "Calendar synced. Open Calendar sync" : "Sync to your calendar"}
      >
        <SyncIcon synced={synced} />
        {synced ? "Calendar synced" : "Sync to your calendar"}
      </Button>
      <CalendarSyncDialog open={open} onOpenChange={setOpen} {...props} sync={sync} />
    </>
  );
}
